/**
 * An entity handle.
 *
 * A number rather than an object, and the number carries a generation in its
 * high bits. That is what makes a stale handle detectable: destroying entity 7
 * and creating a new one reuses slot 7, and code still holding the old handle
 * would otherwise silently address the new entity. With a generation the old
 * handle simply reports dead.
 */
export type Entity = number;

/** Slot bits; the rest is the generation. Twenty bits is a million entities. */
const INDEX_BITS = 20;
const INDEX_MASK = (1 << INDEX_BITS) - 1;

export function entityIndex(entity: Entity): number {
  return entity & INDEX_MASK;
}

export function entityGeneration(entity: Entity): number {
  return entity >>> INDEX_BITS;
}

/** A component store: one map from entity index to value, per component name. */
type Store = Map<number, unknown>;

/**
 * The entity store both engines share.
 *
 * Components are plain objects held in a map per component name — a sparse set
 * rather than archetypes. Archetypes iterate faster once a query is hot, but
 * they make adding or removing a component an entity migration, and they are a
 * great deal of machinery to get wrong. For the scale a browser game runs at,
 * the simple structure is fast enough and is obviously correct, which matters
 * more.
 *
 * ```ts
 * const world = new World();
 * const player = world.create();
 * world.set(player, "position", { x: 0, y: 0 });
 * world.set(player, "health", { hp: 100 });
 *
 * for (const [entity, position] of world.each<{ x: number; y: number }>("position")) {
 *   position.x += 1;
 * }
 * ```
 */
export class World {
  /** Generation per slot. */
  #generations: number[] = [];
  /**
   * Liveness per slot. `#free` alone answered it with an `includes` scan, and
   * `alive` runs once per entity per query, so a game churning bullets made
   * every system quadratic in the size of its free list.
   */
  #live: boolean[] = [];
  #free: number[] = [];
  #stores = new Map<string, Store>();
  #alive = 0;

  /** How many entities exist. */
  get size(): number {
    return this.#alive;
  }

  /** Every component name that has ever been used. */
  get components(): string[] {
    return [...this.#stores.keys()];
  }

  create(): Entity {
    const index = this.#free.pop();
    if (index !== undefined) {
      this.#live[index] = true;
      this.#alive++;
      return (this.#generations[index]! << INDEX_BITS) | index;
    }
    const next = this.#generations.length;
    if (next > INDEX_MASK) throw new Error("webwin: too many entities.");
    this.#generations.push(0);
    this.#live.push(true);
    this.#alive++;
    return next;
  }

  /** True when the handle still names a live entity. */
  alive(entity: Entity): boolean {
    const index = entityIndex(entity);
    if (index >= this.#generations.length) return false;
    return this.#generations[index] === entityGeneration(entity) && this.#live[index] === true;
  }

  /**
   * Destroy an entity and everything hung off it.
   *
   * The generation is bumped so any handle still pointing here goes stale
   * rather than silently addressing whatever is created in this slot next.
   */
  destroy(entity: Entity): boolean {
    if (!this.alive(entity)) return false;
    const index = entityIndex(entity);
    for (const store of this.#stores.values()) store.delete(index);
    this.#generations[index] = (this.#generations[index]! + 1) & (INDEX_MASK >>> 0);
    this.#live[index] = false;
    this.#free.push(index);
    this.#alive--;
    return true;
  }

  /** Remove every entity and component. */
  clear(): void {
    for (const store of this.#stores.values()) store.clear();
    this.#generations.length = 0;
    this.#live.length = 0;
    this.#free.length = 0;
    this.#alive = 0;
  }

  #store(name: string): Store {
    let store = this.#stores.get(name);
    if (!store) this.#stores.set(name, (store = new Map()));
    return store;
  }

  /** Attach or replace a component. Ignored for a dead handle. */
  set<T>(entity: Entity, name: string, value: T): T {
    if (!this.alive(entity)) return value;
    this.#store(name).set(entityIndex(entity), value);
    return value;
  }

  get<T>(entity: Entity, name: string): T | undefined {
    if (!this.alive(entity)) return undefined;
    return this.#stores.get(name)?.get(entityIndex(entity)) as T | undefined;
  }

  has(entity: Entity, name: string): boolean {
    return this.alive(entity) && (this.#stores.get(name)?.has(entityIndex(entity)) ?? false);
  }

  remove(entity: Entity, name: string): boolean {
    if (!this.alive(entity)) return false;
    return this.#stores.get(name)?.delete(entityIndex(entity)) ?? false;
  }

  /**
   * Every live entity carrying `name`, with its component.
   *
   * Iterated from a snapshot of the keys, so a body of the loop may create or
   * destroy entities without invalidating the iteration underneath itself —
   * which a system that spawns on collision will do constantly.
   */
  *each<T>(name: string): Generator<[Entity, T]> {
    const store = this.#stores.get(name);
    if (!store) return;
    for (const index of [...store.keys()]) {
      const value = store.get(index) as T | undefined;
      if (value === undefined) continue;
      const entity = (this.#generations[index]! << INDEX_BITS) | index;
      if (!this.alive(entity)) continue;
      yield [entity, value];
    }
  }

  /**
   * Every live entity carrying *all* of `names`, with those components in the
   * order asked for.
   *
   * Driven from the smallest store rather than the first named, so a query for
   * "position and the six things only the player has" walks six entities and
   * not ten thousand.
   */
  *query(...names: string[]): Generator<[Entity, ...unknown[]]> {
    if (names.length === 0) return;
    const stores = names.map((n) => this.#stores.get(n));
    if (stores.some((s) => s === undefined)) return;

    let smallest = 0;
    for (let i = 1; i < stores.length; i++) {
      if (stores[i]!.size < stores[smallest]!.size) smallest = i;
    }

    for (const index of [...stores[smallest]!.keys()]) {
      const entity = (this.#generations[index]! << INDEX_BITS) | index;
      if (!this.alive(entity)) continue;
      const values: unknown[] = [];
      let complete = true;
      for (const store of stores) {
        const value = store!.get(index);
        if (value === undefined) {
          complete = false;
          break;
        }
        values.push(value);
      }
      if (complete) yield [entity, ...values];
    }
  }

  /** The live entities carrying every one of `names`. */
  find(...names: string[]): Entity[] {
    return [...this.query(...names)].map(([entity]) => entity);
  }
}
